import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  RefreshCw,
  Search,
  CheckCircle,
  Shield,
  AlertTriangle,
  Globe,
} from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  NotificationCard,
  UniversalLoadingState,
  StatusBadge,
  useToast,
} from '@afrilink/ui';
import { supabase } from '@/shared/lib/supabase';
import {
  adminReconcileService,
  type ReconcileResponse,
  type ReconcileOrphan,
} from '@/domains/domains-registry/services/adminReconcileService';
import { formatDate } from '@/shared/utils/format';
import { ConfirmDialog } from '@/shared/components/feedback/ConfirmDialog';

/**
 * AdminDomainReconciliationPage — Sprint Admin-A A-2.
 *
 * Lists every domain in our domains.co.za reseller portfolio that is NOT in
 * `domain_link`, classified as either Orphan (needs to be linked to a customer)
 * or Internal-owned (corporate / parked / test — surfaced for transparency).
 *
 * Closes the gap that produced the original `downlow…` triggering symptom.
 *
 * Per Architect Q3 lock: read-only discovery. Per-row Link button writes the
 * domain_link row via the EF (audit-logged); no auto-linking.
 *
 * Spec: _bmad-output/ADMIN_PORTAL_GAP_ANALYSIS_2026-05-13.md §6 A-2.
 */

interface CustomerOption {
  customer_id: string;
  email: string | null;
  full_name: string | null;
  company: string | null;
}

function ageFromSeconds(seconds: number | null): string {
  if (seconds === null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function AdminDomainReconciliationPage() {
  const { toast } = useToast();
  const [response, setResponse] = useState<ReconcileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyDomain, setBusyDomain] = useState<string | null>(null);
  // F-27: both were window.confirm. Marking a domain internal HIDES it from
  // the orphan list, which is the list that exists to stop a customer domain
  // going unnoticed — so a mis-click here makes a problem invisible.
  const [marking, setMarking] = useState<{ domain: string; direction: 'internal' | 'orphan' } | null>(null);
  const [pickerForOrphan, setPickerForOrphan] = useState<ReconcileOrphan | null>(null);

  async function load(force = false) {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      setError(null);
      // First mount: cheap latest() call to avoid burning a registrar API call on
      // every page load. Force refresh hits the registrar.
      const data = force
        ? await adminReconcileService.discover(true)
        : await adminReconcileService.latest();
      // If latest() returned nothing (no snapshot ever taken), do an initial discover.
      if (!data.snapshot_id && !force) {
        const fresh = await adminReconcileService.discover(false);
        setResponse(fresh);
      } else {
        setResponse(data);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load(false);
  }, []);

  async function onLink(orphan: ReconcileOrphan, customerId: string) {
    setBusyDomain(orphan.domain_name);
    try {
      await adminReconcileService.link(orphan.domain_name, customerId);
      toast({ tone: 'success', title: 'Domain linked', body: orphan.domain_name });
      // Optimistic: drop from local orphans list, increment linked_total.
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              orphans: prev.orphans.filter((o) => o.domain_name !== orphan.domain_name),
              summary: {
                ...prev.summary,
                orphan_total: Math.max(0, prev.summary.orphan_total - 1),
                linked_total: prev.summary.linked_total + 1,
              },
            }
          : prev
      );
    } catch (e: unknown) {
      toast({ tone: 'danger', title: 'Could not link', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusyDomain(null);
      setPickerForOrphan(null);
    }
  }

  async function onMarkInternal(orphan: ReconcileOrphan) {
    setBusyDomain(orphan.domain_name);
    try {
      await adminReconcileService.markInternal(orphan.domain_name);
      toast({ tone: 'success', title: 'Marked as internal', body: orphan.domain_name });
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              orphans: prev.orphans.filter((o) => o.domain_name !== orphan.domain_name),
              internal: [
                ...prev.internal,
                {
                  domain_name: orphan.domain_name,
                  expiry_date: orphan.expiry_date,
                  marked_at: new Date().toISOString(),
                  note: null,
                },
              ],
              summary: {
                ...prev.summary,
                orphan_total: Math.max(0, prev.summary.orphan_total - 1),
                internal_total: prev.summary.internal_total + 1,
              },
            }
          : prev
      );
    } catch (e: unknown) {
      toast({ tone: 'danger', title: 'Could not mark internal', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusyDomain(null);
    }
  }

  async function onUnmarkInternal(domain_name: string) {
    setBusyDomain(domain_name);
    try {
      await adminReconcileService.unmarkInternal(domain_name);
      toast({ tone: 'success', title: 'Unmarked', body: domain_name });
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              internal: prev.internal.filter((i) => i.domain_name !== domain_name),
              summary: {
                ...prev.summary,
                internal_total: Math.max(0, prev.summary.internal_total - 1),
              },
            }
          : prev
      );
    } catch (e: unknown) {
      toast({ tone: 'danger', title: 'Could not unmark', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusyDomain(null);
    }
  }

  if (loading) {
    return (
      <UniversalPage
        title="Domain reconciliation"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Domains', href: '/admin/domains' },
          { label: 'Reconcile' },
        ]}
      >
        <UniversalLoadingState variant="rows" count={5} />
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title="Domain reconciliation"
      description="Domains in the reseller account that are not yet linked to a portal customer."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Domains', href: '/admin/domains' },
        { label: 'Reconcile' },
      ]}
      actions={
        <Button
          variant="marketing"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`mr-1 size-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          {refreshing ? 'Refreshing…' : 'Refresh from registrar'}
        </Button>
      }
    >
      {error && <NotificationCard tone="danger" title="Could not load reconciliation" body={error} />}

      {response && (
        <div className="space-y-6">
          {/* Summary */}
          <Card interactive={false} className="p-5">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <SummaryStat label="Reseller portfolio" value={response.summary.reseller_total} />
              <SummaryStat label="Linked" value={response.summary.linked_total} tone="success" />
              <SummaryStat label="Internal" value={response.summary.internal_total} tone="muted" />
              <SummaryStat label="Orphans" value={response.summary.orphan_total} tone={response.summary.orphan_total > 0 ? 'warning' : 'muted'} />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {response.taken_at
                ? `Snapshot taken ${ageFromSeconds(response.age_seconds)} (${formatDate(response.taken_at)})`
                : 'No snapshot yet'}
              {response.is_stale && (
                <span className="ml-2 text-warning">· Domain link changed since this snapshot — refresh to re-check.</span>
              )}
              {response.cached && !response.is_stale && (
                <span className="ml-2">· Cached</span>
              )}
            </p>
          </Card>

          {/* Orphans */}
          <section>
            <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold tracking-tight text-foreground">
              <AlertTriangle className="size-4 text-warning" aria-hidden />
              Orphans ({response.orphans.length})
            </h2>
            {response.orphans.length === 0 ? (
              <Card interactive={false} className="p-4 text-sm text-muted-foreground">
                Inbox zero — every registrar domain is either linked or marked internal.
              </Card>
            ) : (
              <Card interactive={false} className="overflow-x-auto">
                {/*
                  NO FILTER BAR (2026-09-13). These two tables are the OUTPUT of
                  an on-demand reconciliation run, not a browsable list — a diff
                  you read in full, then act on. §4.2 classified the page "none"
                  for a date because a drift set has no event window, and search
                  over a result you just asked for is chrome rather than help.
                  The customer-picker modal below keeps its own search, which is
                  a different job: finding one row to link.
                */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-card-subtle text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Domain</th>
                      <th className="px-3 py-2">Registrant</th>
                      <th className="px-3 py-2">Expiry</th>
                      <th className="px-3 py-2">Auto-renew</th>
                      <th className="px-3 py-2">Suggested</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.orphans.map((o) => (
                      <tr key={o.domain_name} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 font-mono text-foreground">{o.domain_name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{o.registrant_email ?? '—'}</td>
                        <td className="px-3 py-2 text-foreground">{formatDate(o.expiry_date)}</td>
                        <td className="px-3 py-2">
                          {o.auto_renew ? (
                            <StatusBadge tone="active">On</StatusBadge>
                          ) : (
                            <StatusBadge tone="draft">Off</StatusBadge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {o.suggested_customer_email ?? <span className="italic">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            {o.suggested_customer_id && (
                              <Button
                                variant="marketing"
                                size="sm"
                                disabled={busyDomain === o.domain_name}
                                loading={busyDomain === o.domain_name}
                                onClick={() => onLink(o, o.suggested_customer_id!)}
                              >
                                Link to {o.suggested_customer_email?.split('@')[0]}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyDomain === o.domain_name}
                              onClick={() => setPickerForOrphan(o)}
                            >
                              Choose…
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyDomain === o.domain_name}
                              loading={busyDomain === o.domain_name}
                              onClick={() => setMarking({ domain: o.domain_name, direction: 'internal' })}
                            >
                              Mark internal
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </section>

          {/* Internal */}
          {response.internal.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold tracking-tight text-foreground">
                <Shield className="size-4 text-muted-foreground" aria-hidden />
                Internal-owned ({response.internal.length})
              </h2>
              <Card interactive={false} className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-card-subtle text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Domain</th>
                      <th className="px-3 py-2">Expiry</th>
                      <th className="px-3 py-2">Note</th>
                      <th className="px-3 py-2">Marked</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.internal.map((i) => (
                      <tr key={i.domain_name} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 font-mono text-foreground">{i.domain_name}</td>
                        <td className="px-3 py-2 text-foreground">{formatDate(i.expiry_date)}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{i.note ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(i.marked_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyDomain === i.domain_name}
                            loading={busyDomain === i.domain_name}
                            onClick={() => setMarking({ domain: i.domain_name, direction: 'orphan' })}
                          >
                            Unmark
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>
          )}
        </div>
      )}

      {/* Customer picker modal */}
      {pickerForOrphan && (
        <CustomerPickerModal
          orphan={pickerForOrphan}
          onLink={(customerId) => onLink(pickerForOrphan, customerId)}
          onClose={() => setPickerForOrphan(null)}
          busy={busyDomain === pickerForOrphan.domain_name}
        />
      )}

      <ConfirmDialog
        open={marking !== null}
        onOpenChange={(v) => !v && setMarking(null)}
        title={marking?.direction === 'internal' ? 'Mark as internal?' : 'Return to the orphan list?'}
        description={
          marking?.direction === 'internal'
            ? `${marking.domain} is hidden from the orphan list. Use this only for domains SightHost owns — an unlinked CUSTOMER domain hidden here stops being surfaced anywhere.`
            : marking
              ? `${marking.domain} reappears in the orphan list on the next refresh.`
              : undefined
        }
        confirmLabel={marking?.direction === 'internal' ? 'Mark internal' : 'Unmark'}
        busy={busyDomain !== null}
        onConfirm={() => {
          if (!marking) return;
          const m = marking;
          setMarking(null);
          if (m.direction === 'internal') {
            const orphan = response?.orphans.find((o) => o.domain_name === m.domain);
            if (orphan) void onMarkInternal(orphan);
          } else {
            void onUnmarkInternal(m.domain);
          }
        }}
      />
    </UniversalPage>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function SummaryStat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'success' | 'warning' | 'muted';
}) {
  const TONES: Record<typeof tone, string> = {
    neutral: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    muted: 'text-muted-foreground',
  };
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${TONES[tone]}`}>{value}</p>
    </div>
  );
}

function CustomerPickerModal({
  orphan,
  onLink,
  onClose,
  busy,
}: {
  orphan: ReconcileOrphan;
  onLink: (customerId: string) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [customers, setCustomers] = useState<CustomerOption[] | null>(null);
  const [query, setQuery] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('v_admin_customer_summary')
          .select('customer_id, email, full_name, company')
          .order('email', { ascending: true })
          .limit(500);
        if (error) throw error;
        if (!cancelled) setCustomers((data ?? []) as CustomerOption[]);
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!customers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 100);
    return customers.filter(
      (c) =>
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.full_name ?? '').toLowerCase().includes(q) ||
        (c.company ?? '').toLowerCase().includes(q)
    ).slice(0, 100);
  }, [customers, query]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <Card
        interactive={false}
        className="w-full max-w-lg overflow-hidden p-0"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="border-b border-border bg-card-subtle px-4 py-3">
          <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-foreground">
            <Globe className="size-4 text-info" aria-hidden />
            Link {orphan.domain_name} to…
          </h3>
        </div>
        <div className="space-y-3 p-4">
          <Input
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            placeholder="Search by email, name, or company…"
            autoFocus
          />
          {err && <NotificationCard tone="danger" title="Could not load customers" body={err} />}
          {!customers && !err && <UniversalLoadingState variant="rows" count={4} />}
          {customers && (
            <ul className="max-h-72 overflow-y-auto rounded-sm border border-border">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No matches.</li>
              ) : (
                filtered.map((c) => (
                  <li key={c.customer_id} className="border-b border-border last:border-0">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onLink(c.customer_id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-card-hover disabled:opacity-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">{c.email ?? '(no email)'}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {c.full_name ?? '—'}
                          {c.company ? ` · ${c.company}` : ''}
                        </span>
                      </span>
                      <CheckCircle className="size-4 shrink-0 text-success opacity-0 group-hover:opacity-100" aria-hidden />
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-card-subtle px-4 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}
